import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  Building2,
  History,
  MapPin,
  DollarSign,
  Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCampaignEditPage() {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [states, setStates] = useState([]);
  
  const [overrideData, setOverrideData] = useState({
    is_active: false,
    max_bid: 0,
    states: []
  });

  useEffect(() => {
    fetchData();
  }, [campaignId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('dme_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignRes, statesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/campaigns/${campaignId}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/campaigns/reference/states`)
      ]);
      
      const campaignData = campaignRes.data;
      setCampaign(campaignData);
      setStates(statesRes.data);
      setOverrideData({
        is_active: campaignData.is_active,
        max_bid: campaignData.max_bid || 0,
        states: campaignData.states || []
      });
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Campaign not found');
        navigate('/admin/campaigns');
      } else {
        toast.error('Failed to load campaign');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOverride = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/campaigns/${campaignId}/override`,
        overrideData,
        { headers: getAuthHeaders() }
      );
      toast.success('Campaign updated (admin override logged)');
      navigate('/admin/campaigns');
    } catch (error) {
      toast.error('Failed to override campaign');
    } finally {
      setSaving(false);
    }
  };

  const toggleState = (stateCode) => {
    if (overrideData.states.includes(stateCode)) {
      setOverrideData({ ...overrideData, states: overrideData.states.filter(s => s !== stateCode) });
    } else {
      setOverrideData({ ...overrideData, states: [...overrideData.states, stateCode] });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-campaign-edit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/campaigns')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="w-6 h-6 text-orange-500" />
              Admin Override
            </h1>
            <p className="text-muted-foreground">{campaign?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate(`/admin/campaigns/${campaignId}/history`)}>
            <History className="w-4 h-4 mr-2" />
            View History
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/campaigns')}>
            Cancel
          </Button>
          <Button onClick={handleSaveOverride} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Override
          </Button>
        </div>
      </div>

      {/* Warning Banner */}
      <Card className="border-orange-200 bg-orange-50">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-orange-500" />
            <div>
              <p className="font-medium text-orange-800">Admin Override Mode</p>
              <p className="text-sm text-orange-600">Changes made here will be logged as an admin override and visible to the client.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Override Controls */}
        <div className="col-span-2 space-y-6">
          {/* Client Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Client Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Client:</span>
                  <p className="font-medium">{campaign?.client_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Campaign ID:</span>
                  <p className="font-mono">{campaign?.id}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status Toggle */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Campaign Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="text-base font-medium">Active Status</Label>
                  <p className="text-sm text-muted-foreground">Turn campaign on or off</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={overrideData.is_active ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                    {overrideData.is_active ? 'Active' : 'Paused'}
                  </span>
                  <Switch
                    checked={overrideData.is_active}
                    onCheckedChange={(checked) => setOverrideData({ ...overrideData, is_active: checked })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Max Bid */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Maximum Bid
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <span className="text-lg">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={overrideData.max_bid}
                  onChange={(e) => setOverrideData({ ...overrideData, max_bid: parseFloat(e.target.value) || 0 })}
                  className="w-40 text-lg"
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Current client bid: {formatCurrency(campaign?.max_bid)}
              </p>
            </CardContent>
          </Card>

          {/* State Targeting */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  State Targeting
                </CardTitle>
                <div className="flex gap-2 text-sm">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setOverrideData({ ...overrideData, states: states.map(s => s.code) })}
                    className="text-teal-600"
                  >
                    Select All
                  </Button>
                  <span className="text-muted-foreground">|</span>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setOverrideData({ ...overrideData, states: [] })}
                    className="text-teal-600"
                  >
                    Select None
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {overrideData.states.length} states selected (Client had {campaign?.states?.length || 0} selected)
              </p>
              <div className="grid grid-cols-5 gap-2 max-h-60 overflow-y-auto border rounded-lg p-4">
                {states.map((state) => (
                  <div key={state.code} className="flex items-center gap-2">
                    <Checkbox
                      id={`admin-state-${state.code}`}
                      checked={overrideData.states.includes(state.code)}
                      onCheckedChange={() => toggleState(state.code)}
                    />
                    <label htmlFor={`admin-state-${state.code}`} className="text-sm cursor-pointer">
                      {state.code}
                    </label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Campaign Summary */}
        <div className="space-y-6">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="text-base">Campaign Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">CAMPAIGN NAME</Label>
                <p className="font-medium">{campaign?.name}</p>
              </div>
              
              {campaign?.description && (
                <div>
                  <Label className="text-xs text-muted-foreground">DESCRIPTION</Label>
                  <p className="text-sm">{campaign?.description}</p>
                </div>
              )}
              
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Lead Type:</span>
                  <Badge variant="outline">{campaign?.lead_type}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Bid Type:</span>
                  <Badge variant="outline">{campaign?.bid_type}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Original Status:</span>
                  <Badge variant={campaign?.is_active ? 'default' : 'secondary'}>
                    {campaign?.is_active ? 'Active' : 'Paused'}
                  </Badge>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-xs text-muted-foreground">STATS</Label>
                <div className="grid grid-cols-3 gap-2 mt-2 text-center">
                  <div className="p-2 bg-muted/50 rounded">
                    <div className="font-bold">{campaign?.stats?.leads || 0}</div>
                    <div className="text-xs text-muted-foreground">Leads</div>
                  </div>
                  <div className="p-2 bg-muted/50 rounded">
                    <div className="font-bold">{formatCurrency(campaign?.stats?.spend || 0)}</div>
                    <div className="text-xs text-muted-foreground">Spend</div>
                  </div>
                  <div className="p-2 bg-muted/50 rounded">
                    <div className="font-bold">{formatCurrency(campaign?.stats?.cpl || 0)}</div>
                    <div className="text-xs text-muted-foreground">CPL</div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-xs text-muted-foreground">TIMESTAMPS</Label>
                <div className="space-y-1 mt-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created:</span>
                    <span>{new Date(campaign?.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Updated:</span>
                    <span>{new Date(campaign?.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
